import React from "react";
import moment from "moment";
import { toFixed, toFixedSigned, getWeightedMean, isEmpty, truncate } from "../utils/helpers";
import { colorSecondary, colorTomatoRed, colorLemom, colorLightGreen, colorOrange, colorPurple, colorPrimary } from "../utils/colors";
import { PART_SHELL, UOM } from "../constants";
import { LinkField } from "jazasoft/lib/mui/components/Table";
import SettingKeys from "../utils/SettingKeys";
import Tooltip from "../components/Tooltip";

const lengthKeyMap = {
  120: 0,
  100: 2,
  80: 4,
  60: 6,
  40: 10
};

export const lengthScore = rollLengthVariation => {
  const map = Object.keys(rollLengthVariation).map(key => ({
    key: lengthKeyMap[key],
    weight: rollLengthVariation[key]
  }));
  return getWeightedMean(map);
};

export const widthScore = rollWidthDeviation => {
  return Object.keys(rollWidthDeviation).filter(key => rollWidthDeviation[key] > 0).length;
};

export const getReportSerchText = ({ start, end, customerList, productList, season, millList, fabricPatternList, fabricCategoryList }) => {
  let filters = [];
  if (start) {
    start = !isNaN(start) ? moment(start).valueOf() : moment(start, "YYYY-MM-DD").valueOf();
    filters.push(`orderCloseDate=ge=${start}`);
  }
  if (end) {
    end = !isNaN(end) ? moment(end).valueOf() : moment(end, "YYYY-MM-DD").valueOf();
    filters.push(`orderCloseDate=lt=${end}`);
  }
  if (customerList && customerList.length > 0) {
    filters.push(`customer=in=(${customerList.map(({ name }) => `'${name}'`).join(",")})`);
  }
  if (season) {
    filters.push(`season=='${season}'`);
  }
  if (productList && productList.length > 0) {
    filters.push(`product=in=(${productList.map(({ name }) => `'${name}'`).join(",")})`);
  }
  if (millList && millList.length > 0) {
    filters.push(`mill=in=(${millList.map(({ name }) => `'${name}'`).join(",")})`);
  }
  if (fabricPatternList && fabricPatternList.length > 0) {
    filters.push(`fabricType=in=(${fabricPatternList.map(({ name }) => `'${name}'`).join(",")})`);
  }
  if (fabricCategoryList && fabricCategoryList.length > 0) {
    filters.push(`fabricCategory=in=(${fabricCategoryList.map(({ name }) => `'${name}'`).join(",")})`);
  }

  return filters.join(";");
};

export const DataType = {
  FABRIC_UTILIZATION_SUMMARY: "FabricUtilizationSummary",
  WASTAGE_SUMMMARY: "WastageSummary",
  HIGH_WASTAGE: "HighWastage",
  CONSUMPTION_SUMMARY: "ConsumptionSummary",
  CUT_SUMMARY: "CutSummary",
  INCREASE_IN_CONSUMPTION: "IncreaseInConsumption",
  DECREASE_IN_CONSUMPTION: "DecreaseInConsumption",
  LOW_EFFICIENCY: "PoorEfficiency",
  WIDTH_DEVIATION: "WidthDeviation",
  LENGTH_VARIATION: "LengthVariation",
  CUT_ANALYSIS: "CutAnalysis",
  LOW_CUT_PERCENT: "LowCutPercent",
  MARKER_EFFICIENCY: "MarkerEfficiency"
};

const getValue = (acc, el, key) => el[key] + (acc[el.id] ? acc[el.id][key] || 0 : 0);

const getSubTitle = e => (e.part && e.color ? `${e.style.substring(0, 25)}, ${e.color.substring(0, 35)}, ${e.part}` : "");

export const getEchartOptions = ({ dataTypeList = [], orderList: reportList = [], settings = [], rowCount = 10 }) => {
  let options = {};

  if (dataTypeList.includes(DataType.FABRIC_UTILIZATION_SUMMARY) || dataTypeList.includes(DataType.WASTAGE_SUMMMARY)) {
    const summary = ["available", "usedInLay", "endLoss", "endBits", "damaged", "usedInPanel"].reduce(
      (acc, key) => ({ ...acc, [key]: Math.round(reportList.reduce((acc, order) => acc + order[key], 0)) }),
      {}
    );

    const utilized = summary.usedInLay;
    const wastage = summary.endBits + summary.endLoss + summary.usedInPanel + summary.damaged;

    if (dataTypeList.includes(DataType.FABRIC_UTILIZATION_SUMMARY)) {
      options[DataType.FABRIC_UTILIZATION_SUMMARY] = {
        color: [colorSecondary.main, colorTomatoRed.main],
        tooltip: {
          trigger: "item",
          formatter: "{a} <br/>{b} : {c} ({d}%)"
        },
        legend: {
          x: "center",
          y: "bottom",
          data: ["Utilized", "Wastage"]
        },
        calculable: true,
        series: [
          {
            name: "Fabric Utilization",
            type: "pie",
            radius: "50%",
            center: ["50%", "40%"],
            data: [
              { value: toFixed(utilized), name: "Utilized" },
              { value: toFixed(wastage), name: "Wastage" }
            ],
            itemStyle: {
              normal: {
                label: {
                  position: "outer",
                  formatter: function (params) {
                    return params.percent + "%";
                  }
                },
                labelLine: {
                  show: false
                }
              },
              emphasis: {
                label: {
                  show: true,
                  formatter: "{b}\n{d}%"
                }
              }
            }
          }
        ]
      };
    }

    if (dataTypeList.includes(DataType.WASTAGE_SUMMMARY)) {
      options[DataType.WASTAGE_SUMMMARY] = {
        color: [colorLightGreen.main, colorTomatoRed.main, colorLemom.main, colorSecondary.main],
        tooltip: {
          trigger: "item",
          formatter: "{a} <br/>{b} : {c} ({d}%)"
        },
        legend: {
          x: "center",
          y: "bottom",
          data: ["Used In Panel Replacement", "Damaged", "Actual End Bits", "End Loss"]
        },
        calculable: true,
        series: [
          {
            name: "Wastage",
            type: "pie",
            radius: "50%",
            center: ["50%", "40%"],
            data: [
              { value: toFixed(summary.usedInPanel), name: "Used In Panel Replacement" },
              { value: toFixed(summary.endLoss), name: "End Loss" },
              { value: toFixed(summary.endBits), name: "Actual End Bits" },
              { value: toFixed(summary.damaged), name: "Damaged" }
            ],
            itemStyle: {
              normal: {
                label: {
                  position: "outer",
                  formatter: function (params) {
                    return params.percent + "%";
                  }
                },
                labelLine: {
                  show: false
                }
              },
              emphasis: {
                label: {
                  show: true,
                  formatter: "{b}\n{d}%"
                }
              }
            }
          }
        ]
      };
    }
  }

  if (dataTypeList.includes(DataType.HIGH_WASTAGE)) {
    const reportMap = reportList.reduce(
      (acc, el) => ({
        ...acc,
        [el.id]: {
          ...el,
          ...["allocated", "available", "endBits", "endLoss", "damaged", "usedInPanel"].reduce(
            (prev, key) => ({ ...prev, [key]: getValue(acc, el, key) }),
            {}
          )
        }
      }),
      {}
    );
    const list = Object.keys(reportMap)
      .map(key => reportMap[key])
      .map(e => ({ ...e, wastage: e.endBits + e.endLoss + e.usedInPanel + e.damaged }))
      .filter(e => e.part && e.part.includes(PART_SHELL))
      .map(e => ({ ...e, wastagePercent: e.available > 0 ? (e.wastage * 100) / e.available : 0 }))
      .sort((a, b) => b.wastagePercent - a.wastagePercent)
      .map(e => ({ ...e, title: e.poRef, subTitle: getSubTitle(e), rightTitle: `${toFixed(e.wastagePercent)}%` }))
      .slice(0, 5);
    options[DataType.HIGH_WASTAGE] = list;
  }

  if (dataTypeList.includes(DataType.CONSUMPTION_SUMMARY)) {
    const misCu = getWeightedMean(
      reportList.filter(e => !isEmpty(e.misCu) && e.misCu > 0).map(order => ({ key: order.misCu, weight: order.allocated - order.returned }))
    );
    const actualCu = getWeightedMean(
      reportList.filter(e => !isEmpty(e.actualCu) && e.actualCu > 0).map(order => ({ key: order.actualCu, weight: order.allocated - order.returned }))
    );
    const planCu = getWeightedMean(
      reportList.filter(e => !isEmpty(e.planCu) && e.planCu > 0).map(order => ({ key: order.planCu, weight: order.allocated - order.returned }))
    );
    const stdCu = getWeightedMean(
      reportList.filter(e => !isEmpty(e.stdCu) && e.stdCu > 0).map(order => ({ key: order.stdCu, weight: order.allocated - order.returned }))
    );
    const orderingCu = getWeightedMean(
      reportList
        .filter(e => !isEmpty(e.orderingCu) && e.orderingCu > 0)
        .map(order => ({ key: order.orderingCu, weight: order.allocated - order.returned }))
    );

    let cuSummaryList = [
      { label: "BOM CU", value: toFixed(orderingCu, 3) },
      { label: "Std. CU", value: toFixed(stdCu, 3) },
      { label: "Plan CU", value: toFixed(planCu, 3) },
      { label: "Actual CU", value: toFixed(actualCu, 3) },
      { label: "MIS CU", value: toFixed(misCu, 3) }
    ];
    if (stdCu === 0) {
      cuSummaryList = cuSummaryList.filter(c => c.label !== "Std. CU");
    }

    options[DataType.CONSUMPTION_SUMMARY] = {
      tooltip: {
        trigger: "axis"
      },
      calculable: true,
      xAxis: [
        {
          type: "value",
          boundaryGap: [0, 0.01],
          show: false,
          scale: false,
          min: 0.9 * cuSummaryList.map(e => e.value).sort((a, b) => a - b)[0]
        }
      ],
      grid: {
        left: "1%",
        right: "10%",
        containLabel: true
      },
      yAxis: [
        {
          type: "category",
          data: cuSummaryList.map(e => e.label)
          // data: ["BOM CU", "Lay CU", "Actual CU", "MIS CU"],
          // axisLabel: {
          //   margin: 8,
          //   rotate: 25,
          //   formatter: name => `${name.split(" ")[0]}\n ${name.split(" ")[1]}`
          // }
        }
      ],
      series: [
        {
          name: "Consumption",
          type: "bar",
          barWidth: 20,
          data: cuSummaryList.map(e => e.value), //[toFixed(orderingCu, 3), toFixed(layCu, 3), toFixed(actualCu, 3), toFixed(misCu, 3)],
          itemStyle: {
            normal: {
              color: function (params) {
                // build a color map as your need.
                var colorList = [colorLightGreen.main, colorSecondary.main, colorTomatoRed.main, colorLemom.main];
                return colorList[params.dataIndex];
              },
              label: {
                show: true,
                position: "right",
                formatter: "{c}"
              }
            }
          }
        }
      ]
    };
  }

  if (dataTypeList.includes(DataType.INCREASE_IN_CONSUMPTION)) {
    let setting = settings.find(s => s.key === SettingKeys.INCREASE_IN_CONSUMPTION);
    const sValue = setting ? parseFloat(setting.value) : 0;
    const list = reportList
      .map(e => ({ ...e, diff: e.grossCu - e.orderingCu }))
      .filter(e => e.part && e.part.includes(PART_SHELL) && e.diff > sValue)
      .sort((a, b) => b.diff - a.diff)
      .map(e => ({
        ...e,
        title: e.poRef,
        subTitle: getSubTitle(e),
        rightTitle: `${toFixedSigned(e.diff * 100)} cm`
      }))
      .slice(0, 5);
    options[DataType.INCREASE_IN_CONSUMPTION] = list;
  }

  if (dataTypeList.includes(DataType.DECREASE_IN_CONSUMPTION)) {
    let setting = settings.find(s => s.key === SettingKeys.DECREASE_IN_CONSUMPTION);
    const sValue = setting ? parseFloat(setting.value) : 0;
    const list = reportList
      .map(e => ({ ...e, diff: e.orderingCu - e.grossCu }))
      .filter(e => e.part && e.part.includes(PART_SHELL) && e.diff > sValue)
      .sort((a, b) => b.diff - a.diff)
      .map(e => ({
        ...e,
        title: e.poRef,
        subTitle: getSubTitle(e),
        rightTitle: `${toFixedSigned(-e.diff * 100)} cm`
      }))
      .slice(0, 5);
    options[DataType.DECREASE_IN_CONSUMPTION] = list;
  }

  if (dataTypeList.includes(DataType.LOW_EFFICIENCY)) {
    const columns = [
      {
        dataKey: "poRef",
        title: "PO Ref",
        element: (
          <LinkField
            onClick={({ record }) =>
              _ =>
                record && record.orderId && window.open(`/cutPlan/${record.orderId}/view`, "_blank")}
          />
        )
      },
      { dataKey: "color", title: "Color" },
      { dataKey: "part", title: "Part" },
      { dataKey: "lot", title: "Lot" },
      { dataKey: "efficiency", title: "Efficiency" },
      { dataKey: "repeat", title: "Repeat" },
      { dataKey: "fabDirection", title: "Fab. Direction" },
      { dataKey: "csv", title: "CSV" },
      { dataKey: "actWidth", title: "Actual Width" }
    ];

    const list = reportList.map(e => {
      let category, repeatValue;
      if (e.repeatLength && e.repeatWidth) {
        const repeat = e.repeatLength * e.repeatWidth;
        repeatValue = `${e.repeatLength} X ${e.repeatWidth}`;
        category = repeat < 4 ? "Up to (2x2) cm" : repeat < 16 ? "(2x2) to (4x4) cm" : "Above (4x4) cm";
      } else if (e.repeatLength || e.repeatWidth) {
        const repeat = e.repeatLength || e.repeatWidth;
        repeatValue = `${repeat}`;
        category = repeat < 1 ? "Up to 1 cm" : repeat < 2 ? "1 to 2 cm" : "Above 2 cm";
      } else {
        category = isEmpty(e.csv) || e.csv === "No" ? "Solid: Normal" : "Solid: CSV";
        repeatValue = "-";
      }
      return {
        ...e,
        category,
        repeat: repeatValue,
        actWidth: `${toFixed(e.actualWidth, 1)}cm`,
        efficiency: `${toFixed(e.markerEfficiency, 1)}%`
      };
    });

    options[DataType.LOW_EFFICIENCY] = {
      columns,
      rows: list
    };
  }

  if (dataTypeList.includes(DataType.WIDTH_DEVIATION)) {
    let setting = settings.find(s => s.key === SettingKeys.LESS_ACTUAL_WIDTH);
    const sValue = setting ? parseFloat(setting.value) : 2;
    // Table Data
    const columns = [
      {
        dataKey: "poRef",
        title: "PO Ref",
        element: (
          <LinkField
            onClick={({ record }) =>
              _ =>
                record && record.orderId && window.open(`/reports/micro/reconciliation/${record.orderId}/view`, "_blank")}
          />
        )
      },
      { dataKey: "part", title: "Part" },
      { dataKey: "lot", title: "Lot" },
      { dataKey: "color", title: "Color" },
      { dataKey: "widthDeviation", title: "Width Deviation" },
      { dataKey: "fabric", title: "Fabric" },
      { dataKey: "mill", title: "Mill" }
    ];

    let list = reportList.map(e => {
      const rollWidthDeviation = JSON.parse(e.rollWidthDeviation);
      return { ...e, score: widthScore(rollWidthDeviation), widthDiff: e.actualWidth - e.width };
    });
    let ids = list
      .filter(e => e.available > 500 && e.widthDiff <= -sValue)
      // .filter(e => e.available > 500)
      .sort((a, b) => a.widthDiff - b.widthDiff)
      .slice(0, rowCount / 2)
      .map(e => e.id);
    ids = [
      ...ids,
      ...list
        .filter(e => e.available > 500 && e.score > 2 && !ids.includes(e.id))
        .sort((a, b) => (a.score === b.score ? b.available - a.available : b.score - a.score))
        .slice(0, rowCount - ids.length)
        .map(e => e.id)
    ];

    const orders = reportList.reduce((acc, el) => ({ ...acc, [el.id]: el }), {});

    const rows = ids
      .map(id => orders[id])
      .map(e => {
        let deviation = JSON.parse(e.rollWidthDeviation);
        const keys = Object.keys(deviation)
          .filter(key => deviation[key] > 0)
          .map(key => parseFloat(key))
          .sort((a, b) => a - b);
        return {
          ...e,
          fabric: toFixed(e.available, 0),
          color: e.color.substring(0, 20),
          widthDeviation: `${toFixedSigned(e.actualWidth - e.width, 1)}cm (${keys[0]}cm to ${keys[keys.length - 1]}cm)`
        };
      });

    // Chart Data
    let widthDeviation = {};
    reportList.forEach(e => {
      const rollWidthDeviation = JSON.parse(e.rollWidthDeviation);
      Object.keys(rollWidthDeviation).forEach(key => {
        widthDeviation[key] = (widthDeviation[key] || 0) + rollWidthDeviation[key];
      });
    });

    let dataWidthDeviation = [
      { deviation: "Below -7", qty: widthDeviation["-8"] },
      { deviation: "-7 to -5", qty: widthDeviation["-6"] },
      { deviation: "-5 to -3", qty: widthDeviation["-4"] },
      { deviation: "-3 to -1", qty: widthDeviation["-2"] },
      { deviation: "-1 to +1", qty: widthDeviation["0"] },
      { deviation: "+1 to +3", qty: widthDeviation["2"] },
      { deviation: "+3 to +5", qty: widthDeviation["4"] },
      { deviation: "+5 to +7", qty: widthDeviation["6"] },
      { deviation: "Above +7", qty: widthDeviation["8"] }
    ];

    const totalQty = dataWidthDeviation.reduce((acc, el) => acc + el.qty, 0);
    dataWidthDeviation = dataWidthDeviation.map(e => ({
      ...e,
      qtyPercent: parseFloat(toFixed((e.qty * 100) / totalQty, 1))
    }));

    options[DataType.WIDTH_DEVIATION] = {
      columns,
      rows,
      chartOptions: {
        tooltip: {
          trigger: "axis"
        },
        calculable: true,
        xAxis: [
          {
            type: "category",
            data: dataWidthDeviation.map(e => e.deviation),
            axisLabel: {
              rotate: 45
            }
          }
        ],
        yAxis: [
          {
            type: "value",
            show: false
          }
        ],
        series: [
          {
            name: "Width Deviation",
            type: "bar",
            barWidth: 25,
            data: dataWidthDeviation.map(e => e.qtyPercent),
            itemStyle: {
              normal: {
                color: colorSecondary.main,
                label: {
                  show: true,
                  position: "top",
                  formatter: "{c}%"
                }
              }
            }
          }
        ]
      }
    };
  }

  if (dataTypeList.includes(DataType.LENGTH_VARIATION)) {
    // Table Data
    const columns = [
      {
        dataKey: "poRef",
        title: "PO Ref",
        element: (
          <LinkField
            onClick={({ record }) =>
              _ =>
                record && record.orderId && window.open(`/reports/micro/reconciliation/${record.orderId}/view`, "_blank")}
          />
        )
      },
      { dataKey: "part", title: "Part" },
      { dataKey: "lot", title: "Lot" },
      { dataKey: "color", title: "Color" },
      { dataKey: "avgRollLength", title: "Avg. Length" },
      { dataKey: "fabric", title: "Fabric" },
      { dataKey: "mill", title: "Mill" }
    ];

    const list = reportList
      .map(e => ({
        ...e,
        avgRollLength: e.rollCount > 0 ? e.available / (e.rollCount - e.splitCount) : 0,
        fabric: toFixed(e.available, 0),
        color: e.color.substring(0, 20)
      }))
      .filter(e => e.available > 500 && e.avgRollLength < 90)
      .sort((a, b) => a.avgRollLength - b.avgRollLength)
      .slice(0, rowCount)
      .map(e => ({ ...e, avgRollLength: toFixed(e.avgRollLength, 0) }));

    // Chart Data
    let rollVariation = {};
    reportList.forEach(e => {
      const rollLengthVariation = JSON.parse(e.rollLengthVariation);
      Object.keys(rollLengthVariation).forEach(key => {
        rollVariation[key] = (rollVariation[key] || 0) + rollLengthVariation[key];
      });
    });
    let dataLengthVariation = [
      { range: "0-40", qty: rollVariation["40"] },
      { range: "40-60", qty: rollVariation["60"] },
      { range: "60-80", qty: rollVariation["80"] },
      { range: "80-100", qty: rollVariation["100"] },
      { range: "100+", qty: rollVariation["120"] }
    ];

    const totalQty = dataLengthVariation.reduce((acc, el) => acc + el.qty, 0);
    dataLengthVariation = dataLengthVariation.map(e => ({
      ...e,
      qtyPercent: parseFloat(toFixed((e.qty * 100) / totalQty, 1))
    }));

    options[DataType.LENGTH_VARIATION] = {
      columns,
      rows: list,
      chartOptions: {
        tooltip: {
          trigger: "axis"
        },
        calculable: true,
        xAxis: [
          {
            type: "category",
            data: dataLengthVariation.map(e => e.range),
            axisLabel: {
              rotate: 45
            }
          }
        ],
        yAxis: [
          {
            type: "value",
            show: false
          }
        ],
        series: [
          {
            name: "Length Variation",
            type: "bar",
            data: dataLengthVariation.map(e => e.qtyPercent),
            barWidth: 25,
            itemStyle: {
              normal: {
                color: colorSecondary.main,
                label: {
                  show: true,
                  position: "top",
                  formatter: "{c}%"
                }
              }
            }
          }
        ]
      }
    };
  }

  if (dataTypeList.includes(DataType.CUT_ANALYSIS) || dataTypeList.includes(DataType.LOW_CUT_PERCENT)) {
    let list = reportList.filter(e => e.lot === e.noOfLots).map(e => ({ ...e, month: moment(e.orderCloseDate).format("MMM, YY") }));

    let cutSummary = {
      "-6": { poCount: 0, cutQty: 0 },
      "-4": { poCount: 0, cutQty: 0 },
      "-2": { poCount: 0, cutQty: 0 },
      0: { poCount: 0, cutQty: 0 },
      "+2": { poCount: 0, cutQty: 0 },
      "+4": { poCount: 0, cutQty: 0 },
      "+6": { poCount: 0, cutQty: 0 }
    };
    let totalQty = 0;
    list.forEach(order => {
      const { orderQty, cumCutQty: cutQty } = order;
      totalQty += cutQty;
      const cutPercent = parseFloat(toFixed((cutQty * 100) / orderQty, 2)) - 100.0;
      if (cutPercent < -5.0) {
        cutSummary["-6"] = {
          poCount: cutSummary["-6"]["poCount"] + 1,
          cutQty: cutSummary["-6"]["cutQty"] + cutQty
        };
      } else if (cutPercent < -3.0) {
        cutSummary["-4"] = {
          poCount: cutSummary["-4"]["poCount"] + 1,
          cutQty: cutSummary["-4"]["cutQty"] + cutQty
        };
      } else if (cutPercent < -1.0) {
        cutSummary["-2"] = {
          poCount: cutSummary["-2"]["poCount"] + 1,
          cutQty: cutSummary["-2"]["cutQty"] + cutQty
        };
      } else if (cutPercent < 1.0) {
        cutSummary["0"] = {
          poCount: cutSummary["0"]["poCount"] + 1,
          cutQty: cutSummary["0"]["cutQty"] + cutQty
        };
      } else if (cutPercent < 3.0) {
        cutSummary["+2"] = {
          poCount: cutSummary["+2"]["poCount"] + 1,
          cutQty: cutSummary["+2"]["cutQty"] + cutQty
        };
      } else if (cutPercent < 5.0) {
        cutSummary["+4"] = {
          poCount: cutSummary["+4"]["poCount"] + 1,
          cutQty: cutSummary["+4"]["cutQty"] + cutQty
        };
      } else {
        cutSummary["+6"] = {
          poCount: cutSummary["+6"]["poCount"] + 1,
          cutQty: cutSummary["+6"]["cutQty"] + cutQty
        };
      }
    });

    const dataCutDeviation = [
      {
        cutPercent: "Below -5%",
        ...cutSummary["-6"],
        "cutQty(%)": parseFloat(toFixed((cutSummary["-6"].cutQty * 100) / totalQty, 1))
      },
      {
        cutPercent: "-5% to -3%",
        ...cutSummary["-4"],
        "cutQty(%)": parseFloat(toFixed((cutSummary["-4"].cutQty * 100) / totalQty, 1))
      },
      {
        cutPercent: "-3% to -1%",
        ...cutSummary["-2"],
        "cutQty(%)": parseFloat(toFixed((cutSummary["-2"].cutQty * 100) / totalQty, 1))
      },
      {
        cutPercent: "-1% to +1%",
        ...cutSummary["0"],
        "cutQty(%)": parseFloat(toFixed((cutSummary["0"].cutQty * 100) / totalQty, 1))
      },
      {
        cutPercent: "+1% to +3%",
        ...cutSummary["+2"],
        "cutQty(%)": parseFloat(toFixed((cutSummary["+2"].cutQty * 100) / totalQty, 1))
      },
      {
        cutPercent: "+3% to +5%",
        ...cutSummary["+4"],
        "cutQty(%)": parseFloat(toFixed((cutSummary["+4"].cutQty * 100) / totalQty, 1))
      },
      {
        cutPercent: "Above +5%",
        ...cutSummary["+6"],
        "cutQty(%)": parseFloat(toFixed((cutSummary["+6"].cutQty * 100) / totalQty, 1))
      }
    ];

    options[DataType.CUT_ANALYSIS] = {
      tooltip: {
        trigger: "axis"
      },
      calculable: true,
      xAxis: [
        {
          type: "category",
          data: dataCutDeviation.map(e => e.cutPercent),
          axisLabel: {
            rotate: 45
          }
        }
      ],
      yAxis: [
        {
          type: "value",
          show: false
        }
      ],
      series: [
        {
          name: "Cut Percent Deviation",
          type: "bar",
          barWidth: 25,
          data: dataCutDeviation.map(e => e["cutQty(%)"]),
          itemStyle: {
            normal: {
              color: colorSecondary.main,
              label: {
                show: true,
                position: "top",
                formatter: "{c}%"
              }
            }
          }
        }
      ]
    };

    const rows = list
      .map(e => ({ ...e, cutPercent: (e.cumCutQty * 100) / e.orderQty }))
      .filter(e => e.lot === e.noOfLots && e.cutPercent < 100)
      .sort((a, b) => a.cutPercent - b.cutPercent)
      .map(e => ({
        ...e,
        title: e.poRef,
        subTitle: `${e.color.substring(0, 30)}, ${e.orderQty} Pcs`,
        rightTitle: `${toFixed(e.cutPercent, 1)}%`
      }))
      .slice(0, 5);
    options[DataType.LOW_CUT_PERCENT] = rows;
  }

  return options;
};

export const getSummaryEchartOptions = ({ dataTypeList = [], reportList = [], settings = [], uom = UOM.METER.value }) => {
  if (isEmpty(reportList)) return {};
  let options = {};

  if (dataTypeList.includes(DataType.FABRIC_UTILIZATION_SUMMARY) || dataTypeList.includes(DataType.WASTAGE_SUMMMARY)) {
    const summary = [
      "allocated",
      "blanketQty",
      "issued",
      "shortage",
      "excess",
      "returned",
      "usedInLay",
      "endLoss",
      "netEndBits",
      "damaged",
      "usedInPanel",
      "missing",
      "unused",
      "allocatedWeight",
      "blanketWeight",
      "issuedWeight",
      "returnedWeight",
      "usedInLayWeight",
      "endLossWeight",
      "netEndBitsWeight",
      "damagedWeight",
      "usedInPanelWeight",
      "missingWeight",
      "unusedWeight"
    ].reduce((acc, key) => ({ ...acc, [key]: reportList.reduce((acc, report) => acc + (report[key] || 0), 0) }), {});

    let utilized = 0;
    let wastage = 0;
    if (uom === UOM.METER.value) {
      const totalConsumed = summary.issued + summary.blanketQty - summary.returned;
      const shortageExcess = summary.shortage + summary.excess;
      const netConsumed = totalConsumed + shortageExcess;
      wastage = summary.blanketQty + summary.netEndBits + summary.endLoss + summary.usedInPanel + summary.damaged + summary.missing + summary.unused;
      utilized = Math.max(0, netConsumed - wastage);
    } else {
      const totalConsumed = summary.issuedWeight + summary.blanketWeight - summary.returnedWeight;
      const shortageExcess = 0;
      const netConsumed = totalConsumed + shortageExcess;
      wastage =
        summary.blanketWeight +
        summary.netEndBitsWeight +
        summary.endLossWeight +
        summary.usedInPanelWeight +
        summary.damagedWeight +
        summary.missingWeight +
        summary.unusedWeight;
      utilized = Math.max(0, netConsumed - wastage);
    }

    if (dataTypeList.includes(DataType.FABRIC_UTILIZATION_SUMMARY)) {
      options[DataType.FABRIC_UTILIZATION_SUMMARY] = {
        color: [colorSecondary.main, colorTomatoRed.main],
        tooltip: {
          trigger: "item",
          formatter: "{a} <br/>{b} : {c} ({d}%)"
        },
        legend: {
          x: "center",
          y: "bottom",
          data: ["Utilized", "Wastage"]
        },
        calculable: true,
        series: [
          {
            name: "Fabric Utilization",
            type: "pie",
            radius: "50%",
            center: ["50%", "40%"],
            data: [
              { value: toFixed(utilized, 0), name: "Utilized" },
              { value: toFixed(wastage, 0), name: "Wastage" }
            ],
            itemStyle: {
              normal: {
                label: {
                  position: "outer",
                  formatter: function (params) {
                    return params.percent + "%";
                  }
                },
                labelLine: {
                  show: false
                }
              },
              emphasis: {
                label: {
                  show: true,
                  formatter: "{b}\n{d}%"
                }
              }
            }
          }
        ]
      };
    }

    if (dataTypeList.includes(DataType.WASTAGE_SUMMMARY)) {
      options[DataType.WASTAGE_SUMMMARY] = {
        color: [
          colorLightGreen.main,
          colorTomatoRed.main,
          colorLemom.main,
          colorSecondary.main,
          colorOrange.main,
          colorPrimary.main,
          colorPurple.main
        ],
        tooltip: {
          trigger: "item",
          formatter: "{a} <br/>{b} : {c} ({d}%)"
        },
        legend: {
          x: "center",
          y: "bottom",
          data: ["Panel Replacement", "End Bits", "End Loss", "Damaged", "Testing Loss", "Missing", "Unused"]
        },
        calculable: true,
        series: [
          {
            name: "Wastage",
            type: "pie",
            radius: "50%",
            center: ["50%", "40%"],
            data:
              uom === UOM.METER.value
                ? [
                    { value: toFixed(summary.usedInPanel), name: "Panel Replacement" },
                    { value: toFixed(summary.endLoss), name: "End Loss" },
                    { value: toFixed(summary.netEndBits), name: "End Bits" },
                    { value: toFixed(summary.damaged), name: "Damaged" },
                    { value: toFixed(summary.blanketQty), name: "Testing Loss" },
                    { value: toFixed(summary.missing), name: "Missing" },
                    { value: toFixed(summary.unused), name: "Unused" }
                  ]
                : [
                    { value: toFixed(summary.usedInPanelWeight), name: "Panel Replacement" },
                    { value: toFixed(summary.endLossWeight), name: "End Loss" },
                    { value: toFixed(summary.netEndBitsWeight), name: "End Bits" },
                    { value: toFixed(summary.damagedWeight), name: "Damaged" },
                    { value: toFixed(summary.blanketWeight), name: "Testing Loss" },
                    { value: toFixed(summary.missingWeight), name: "Missing" },
                    { value: toFixed(summary.unusedWeight), name: "Unused" }
                  ],
            itemStyle: {
              normal: {
                label: {
                  position: "outer",
                  formatter: function (params) {
                    return params.percent + "%";
                  }
                },
                labelLine: {
                  show: false
                }
              },
              emphasis: {
                label: {
                  show: true,
                  formatter: "{b}\n{d}%"
                }
              }
            }
          }
        ]
      };
    }
  }

  if (dataTypeList.includes(DataType.HIGH_WASTAGE)) {
    const reportMap = reportList.reduce(
      (acc, el) => ({
        ...acc,
        [el.id]: {
          ...el,
          ...["issued", "returned", "netEndBits", "endLoss", "damaged", "usedInPanel", "missing", "unused"].reduce(
            (prev, key) => ({ ...prev, [key]: getValue(acc, el, key) }),
            {}
          )
        }
      }),
      {}
    );
    const list = Object.keys(reportMap)
      .map(key => reportMap[key])
      .map(e => ({
        ...e,
        netIssued: Math.max(0, e.issued - e.returned),
        wastage: e.netEndBits + e.endLoss + e.usedInPanel + e.damaged + e.missing + e.unused + e?.blanketQty
      }))
      .filter(e => e.part === PART_SHELL)
      .map(e => ({ ...e, wastagePercent: e.netIssued > 0 ? (e.wastage * 100) / e.netIssued : 0 }))
      .sort((a, b) => b.wastagePercent - a.wastagePercent)
      .map(e => ({ ...e, title: e.poRef, subTitle: getSubTitle(e), rightTitle: `${toFixed(e.wastagePercent)}%` }))
      .slice(0, 5);
    options[DataType.HIGH_WASTAGE] = list;
  }

  if (dataTypeList.includes(DataType.CONSUMPTION_SUMMARY)) {
    const grossCu =
      uom === UOM.METER.value
        ? getWeightedMean(
            reportList.filter(e => !isEmpty(e.grossCu) && e.grossCu > 0).map(order => ({ key: order.grossCu, weight: order.issued - order.returned }))
          )
        : getWeightedMean(
            reportList
              .filter(e => !isEmpty(e.grossCuInKg) && e.grossCuInKg > 0)
              .map(order => ({ key: order.grossCuInKg, weight: order.issued - order.returned }))
          );
    const actualCu =
      uom === UOM.METER.value
        ? getWeightedMean(
            reportList
              .filter(e => !isEmpty(e.actualCu) && e.actualCu > 0)
              .map(order => ({ key: order.actualCu, weight: order.issued - order.returned }))
          )
        : getWeightedMean(
            reportList
              .filter(e => !isEmpty(e.actualCuInKg) && e.actualCuInKg > 0)
              .map(order => ({ key: order.actualCuInKg, weight: order.issued - order.returned }))
          );
    const planCu =
      uom === UOM.METER.value
        ? getWeightedMean(
            reportList.filter(e => !isEmpty(e.planCu) && e.planCu > 0).map(order => ({ key: order.planCu, weight: order.issued - order.returned }))
          )
        : getWeightedMean(
            reportList
              .filter(e => !isEmpty(e.planCuInKg) && e.planCuInKg > 0)
              .map(order => ({ key: order.planCuInKg, weight: order.issued - order.returned }))
          );
    const stdCu =
      uom === UOM.METER.value
        ? getWeightedMean(
            reportList.filter(e => !isEmpty(e.stdCu) && e.stdCu > 0).map(order => ({ key: order.stdCu, weight: order.issued - order.returned }))
          )
        : getWeightedMean(
            reportList
              .filter(e => !isEmpty(e.stdCuInKg) && e.stdCuInKg > 0)
              .map(order => ({ key: order.stdCuInKg, weight: order.issued - order.returned }))
          );
    const orderingCu =
      uom === UOM.METER.value
        ? getWeightedMean(
            reportList
              .filter(e => !isEmpty(e.orderingCu) && e.orderingCu > 0)
              .map(order => ({ key: order.orderingCu, weight: order.allocated - order.returned }))
          )
        : getWeightedMean(
            reportList
              .filter(e => !isEmpty(e.orderingCuInKg) && e.orderingCuInKg > 0)
              .map(order => ({ key: order.orderingCuInKg, weight: order.allocated - order.returned }))
          );

    let cuSummaryList = [
      { label: "BOM CU", value: toFixed(orderingCu, 3) },
      { label: "Std. CU", value: toFixed(stdCu, 3) },
      { label: "Plan CU", value: toFixed(planCu, 3) },
      { label: "Actual CU", value: toFixed(actualCu, 3) },
      { label: "Gross CU", value: toFixed(grossCu, 3) }
    ];
    if (stdCu === 0) {
      cuSummaryList = cuSummaryList.filter(c => c.label !== "Std. CU");
    }
    const status = reportList.map(e => e.status).filter(e => !isEmpty(e))[0];
    if (!isEmpty(status) && status !== "Closed") {
      cuSummaryList = cuSummaryList.filter(c => c.label !== "Gross CU");
    }

    options[DataType.CONSUMPTION_SUMMARY] = {
      tooltip: {
        trigger: "axis"
      },
      calculable: true,
      xAxis: [
        {
          type: "value",
          boundaryGap: [0, 0.01],
          show: false,
          scale: false,
          min: 0.9 * cuSummaryList.map(e => e.value).sort((a, b) => a - b)[0]
        }
      ],
      grid: {
        left: "1%",
        right: "10%",
        containLabel: true
      },
      yAxis: [
        {
          type: "category",
          data: cuSummaryList.map(e => e.label)
          // data: ["BOM CU", "Lay CU", "Actual CU", "MIS CU"],
          // axisLabel: {
          //   margin: 8,
          //   rotate: 25,
          //   formatter: name => `${name.split(" ")[0]}\n ${name.split(" ")[1]}`
          // }
        }
      ],
      series: [
        {
          name: "Consumption",
          type: "bar",
          barWidth: 20,
          data: cuSummaryList.map(e => e.value), //[toFixed(orderingCu, 3), toFixed(layCu, 3), toFixed(actualCu, 3), toFixed(misCu, 3)],
          itemStyle: {
            normal: {
              color: function (params) {
                // build a color map as your need.
                var colorList = [colorLightGreen.main, colorSecondary.main, colorTomatoRed.main, colorLemom.main, colorPurple.main];
                return colorList[params.dataIndex];
              },
              label: {
                show: true,
                position: "right",
                formatter: "{c}"
              }
            }
          }
        }
      ]
    };
  }

  if (dataTypeList.includes(DataType.CUT_SUMMARY)) {
    const cutQty = reportList.reduce((acc, e) => acc + e.cutQty, 0);
    const planQty = reportList.reduce((acc, e) => acc + (e.plannedQty || 0), 0);
    const orderQty = reportList.reduce((acc, e) => acc + e.cutOrderQty, 0);
    const planPercent = orderQty > 0 ? (planQty * 100) / orderQty : 0.0;
    const cutPercent = orderQty > 0 ? (cutQty * 100) / orderQty : 0.0;

    const cutSummaryList = [
      { label: "Order Qty", value: orderQty, percent: 100 },
      { label: "Plan Qty", value: planQty, percent: toFixed(planPercent, 0) },
      { label: "Cut Qty", value: cutQty, percent: toFixed(cutPercent, 0) }
    ];

    options[DataType.CUT_SUMMARY] = {
      tooltip: {
        trigger: "axis"
      },
      calculable: true,
      xAxis: [
        {
          type: "value",
          boundaryGap: [0, 0.01],
          show: false,
          scale: false,
          min: 0.9 * cutSummaryList.map(e => e.value).sort((a, b) => a - b)[0]
        }
      ],
      grid: {
        left: "1%",
        right: "20%",
        containLabel: true
      },
      yAxis: [
        {
          type: "category",
          data: cutSummaryList.map(e => e.label)
        }
      ],
      series: [
        {
          name: "Pcs",
          type: "bar",
          barWidth: 20,
          data: cutSummaryList.map(e => e.value),
          itemStyle: {
            normal: {
              color: function (params) {
                // build a color map as your need.
                var colorList = [colorLightGreen.main, colorSecondary.main, colorPurple.main];
                return colorList[params.dataIndex];
              },
              label: {
                show: true,
                position: "right",
                // formatter: "{c}"
                formatter: param => {
                  const row = cutSummaryList[param.dataIndex];
                  return `${param.value} (${row.percent}%)`;
                }
              }
            }
          }
        }
      ]
    };
  }

  if (dataTypeList.includes(DataType.INCREASE_IN_CONSUMPTION)) {
    let setting = settings.find(s => s.key === SettingKeys.INCREASE_IN_CONSUMPTION);
    const sValue = setting ? parseFloat(setting.value) : 0;
    const list = reportList
      .map(e => ({ ...e, diff: e.grossCu - e.orderingCu }))
      .filter(e => e.part === PART_SHELL && e.diff > sValue)
      .sort((a, b) => b.diff - a.diff)
      .map(e => ({
        ...e,
        title: e.poRef,
        subTitle: getSubTitle(e),
        rightTitle: `${toFixedSigned(e.diff * 100)} cm`
      }))
      .slice(0, 5);
    options[DataType.INCREASE_IN_CONSUMPTION] = list;
  }

  if (dataTypeList.includes(DataType.DECREASE_IN_CONSUMPTION)) {
    let setting = settings.find(s => s.key === SettingKeys.DECREASE_IN_CONSUMPTION);
    const sValue = setting ? parseFloat(setting.value) : 0;
    const list = reportList
      .map(e => ({ ...e, diff: e.orderingCu - e.grossCu }))
      .filter(e => e.part === PART_SHELL && e.diff > sValue)
      .sort((a, b) => b.diff - a.diff)
      .map(e => ({
        ...e,
        title: e.poRef,
        subTitle: getSubTitle(e),
        rightTitle: `${toFixedSigned(-e.diff * 100)} cm`
      }))
      .slice(0, 5);
    options[DataType.DECREASE_IN_CONSUMPTION] = list;
  }

  if (dataTypeList.includes(DataType.LOW_EFFICIENCY)) {
    const columns = [
      {
        dataKey: "poRef",
        title: "PO Ref",
        element: (
          <LinkField
            onClick={({ record }) =>
              () =>
                !isEmpty(record?.orderId) &&
                window.open(`reports/micro/reconciliation/${record.orderId}/view?style=${record.style}&color=${record.color}`, "_blank")}
          />
        )
      },
      {
        dataKey: "color",
        title: "Color",
        element: ({ record, column }) => (
          <Tooltip title={record[column.dataKey]}>
            <span>{truncate(record[column.dataKey], 25)}</span>
          </Tooltip>
        )
      },
      { dataKey: "part", title: "Part" },
      { dataKey: "efficiency", title: "Efficiency" },
      { dataKey: "repeat", title: "Repeat" },
      { dataKey: "fabDirection", title: "Fab. Direction" },
      { dataKey: "csv", title: "CSV" },
      { dataKey: "actWidth", title: "Actual Width" }
    ];

    const list = reportList.map(e => {
      let category, repeatValue;
      if (e.repeatLength && e.repeatWidth) {
        const repeat = e.repeatLength * e.repeatWidth;
        repeatValue = `${e.repeatLength} X ${e.repeatWidth}`;
        category = repeat < 4 ? "Up to (2x2) cm" : repeat < 16 ? "(2x2) to (4x4) cm" : "Above (4x4) cm";
      } else if (e.repeatLength || e.repeatWidth) {
        const repeat = e.repeatLength || e.repeatWidth;
        repeatValue = `${repeat}`;
        category = repeat < 1 ? "Up to 1 cm" : repeat < 2 ? "1 to 2 cm" : "Above 2 cm";
      } else {
        category = isEmpty(e.csv) || e.csv === "No" ? "Solid: Normal" : "Solid: CSV";
        repeatValue = "-";
      }
      return {
        ...e,
        category,
        repeat: repeatValue,
        actWidth: `${toFixed(e.actualWidth, 1)} cm`,
        efficiency: `${toFixed(e.markerEfficiency, 1)}%`
      };
    });

    options[DataType.LOW_EFFICIENCY] = { columns, rows: list };
  }

  if (dataTypeList.includes(DataType.MARKER_EFFICIENCY)) {
    const efficiencyMap = reportList.map(({ markerEfficiency, cutQty }) => ({ key: markerEfficiency, weight: cutQty }));
    const efficiency = getWeightedMean(efficiencyMap);
    options[DataType.MARKER_EFFICIENCY] = {
      tooltip: {
        formatter: "{a} <br/>{b} : {c}%"
      },
      series: [
        {
          name: "Efficiency",
          type: "gauge",
          min: 50,
          max: 100,
          splitNumber: 5, // Split the number of segments, the default is 5
          axisLine: {
            lineStyle: {
              color: [
                [0.5, colorTomatoRed.main],
                [0.8, colorSecondary.main],
                [1, colorLightGreen.main]
              ],
              width: 5
            }
          },
          axisTick: {
            splitNumber: 10, // How many segments per split
            length: 12, // Attribute length control line length
            lineStyle: {
              color: "auto"
            }
          },
          axisLabel: {
            textStyle: {
              color: "auto"
            }
          },
          splitLine: {
            show: true, // The default display, the property show control is displayed or not
            length: 20, // Attribute length control line length
            lineStyle: {
              color: "auto"
            }
          },
          pointer: {
            width: 5
          },
          detail: {
            formatter: "{value}%",
            textStyle: {
              color: "auto",
              fontWeight: "bold",
              fontSize: 18
            }
          },
          data: [{ value: toFixed(efficiency) }]
        }
      ]
    };
  }

  // if (dataTypeList.includes(DataType.WIDTH_DEVIATION)) {
  //   let setting = settings.find(s => s.key === SettingKeys.LESS_ACTUAL_WIDTH);
  //   const sValue = setting ? parseFloat(setting.value) : 2;
  //   // Table Data
  //   const columns = [
  //     {
  //       dataKey: "poRef",
  //       title: "PO Ref",
  //       element: (
  //         <LinkField
  //           onClick={({ record }) =>
  //             _ =>
  //               record && record.orderId && window.open(`/reports/micro/reconciliation/${record.orderId}/view`, "_blank")}
  //         />
  //       )
  //     },
  //     { dataKey: "part", title: "Part" },
  //     { dataKey: "lot", title: "Lot" },
  //     { dataKey: "color", title: "Color" },
  //     { dataKey: "widthDeviation", title: "Width Deviation" },
  //     { dataKey: "fabric", title: "Fabric" },
  //     { dataKey: "mill", title: "Mill" }
  //   ];

  //   let list = reportList.map(e => {
  //     const rollWidthDeviation = JSON.parse(e.rollWidthDeviation);
  //     return { ...e, score: widthScore(rollWidthDeviation), widthDiff: e.actualWidth - e.width };
  //   });
  //   let ids = list
  //     .filter(e => e.available > 500 && e.widthDiff <= -sValue)
  //     // .filter(e => e.available > 500)
  //     .sort((a, b) => a.widthDiff - b.widthDiff)
  //     .slice(0, rowCount / 2)
  //     .map(e => e.id);
  //   ids = [
  //     ...ids,
  //     ...list
  //       .filter(e => e.available > 500 && e.score > 2 && !ids.includes(e.id))
  //       .sort((a, b) => (a.score === b.score ? b.available - a.available : b.score - a.score))
  //       .slice(0, rowCount - ids.length)
  //       .map(e => e.id)
  //   ];

  //   const orders = reportList.reduce((acc, el) => ({ ...acc, [el.id]: el }), {});

  //   const rows = ids
  //     .map(id => orders[id])
  //     .map(e => {
  //       let deviation = JSON.parse(e.rollWidthDeviation);
  //       const keys = Object.keys(deviation)
  //         .filter(key => deviation[key] > 0)
  //         .map(key => parseFloat(key))
  //         .sort((a, b) => a - b);
  //       return {
  //         ...e,
  //         fabric: toFixed(e.available, 0),
  //         color: e.color.substring(0, 20),
  //         widthDeviation: `${toFixedSigned(e.actualWidth - e.width, 1)}cm (${keys[0]}cm to ${keys[keys.length - 1]}cm)`
  //       };
  //     });

  //   // Chart Data
  //   let widthDeviation = {};
  //   reportList.forEach(e => {
  //     const rollWidthDeviation = JSON.parse(e.rollWidthDeviation);
  //     Object.keys(rollWidthDeviation).forEach(key => {
  //       widthDeviation[key] = (widthDeviation[key] || 0) + rollWidthDeviation[key];
  //     });
  //   });

  //   let dataWidthDeviation = [
  //     { deviation: "Below -7", qty: widthDeviation["-8"] },
  //     { deviation: "-7 to -5", qty: widthDeviation["-6"] },
  //     { deviation: "-5 to -3", qty: widthDeviation["-4"] },
  //     { deviation: "-3 to -1", qty: widthDeviation["-2"] },
  //     { deviation: "-1 to +1", qty: widthDeviation["0"] },
  //     { deviation: "+1 to +3", qty: widthDeviation["2"] },
  //     { deviation: "+3 to +5", qty: widthDeviation["4"] },
  //     { deviation: "+5 to +7", qty: widthDeviation["6"] },
  //     { deviation: "Above +7", qty: widthDeviation["8"] }
  //   ];

  //   const totalQty = dataWidthDeviation.reduce((acc, el) => acc + el.qty, 0);
  //   dataWidthDeviation = dataWidthDeviation.map(e => ({
  //     ...e,
  //     qtyPercent: parseFloat(toFixed((e.qty * 100) / totalQty, 1))
  //   }));

  //   options[DataType.WIDTH_DEVIATION] = {
  //     columns,
  //     rows,
  //     chartOptions: {
  //       tooltip: {
  //         trigger: "axis"
  //       },
  //       calculable: true,
  //       xAxis: [
  //         {
  //           type: "category",
  //           data: dataWidthDeviation.map(e => e.deviation),
  //           axisLabel: {
  //             rotate: 45
  //           }
  //         }
  //       ],
  //       yAxis: [
  //         {
  //           type: "value",
  //           show: false
  //         }
  //       ],
  //       series: [
  //         {
  //           name: "Width Deviation",
  //           type: "bar",
  //           barWidth: 25,
  //           data: dataWidthDeviation.map(e => e.qtyPercent),
  //           itemStyle: {
  //             normal: {
  //               color: colorSecondary.main,
  //               label: {
  //                 show: true,
  //                 position: "top",
  //                 formatter: "{c}%"
  //               }
  //             }
  //           }
  //         }
  //       ]
  //     }
  //   };
  // }

  // if (dataTypeList.includes(DataType.LENGTH_VARIATION)) {
  //   // Table Data
  //   const columns = [
  //     {
  //       dataKey: "poRef",
  //       title: "PO Ref",
  //       element: (
  //         <LinkField
  //           onClick={({ record }) =>
  //             _ =>
  //               record && record.orderId && window.open(`/reports/micro/reconciliation/${record.orderId}/view`, "_blank")}
  //         />
  //       )
  //     },
  //     { dataKey: "part", title: "Part" },
  //     { dataKey: "lot", title: "Lot" },
  //     { dataKey: "color", title: "Color" },
  //     { dataKey: "avgRollLength", title: "Avg. Length" },
  //     { dataKey: "fabric", title: "Fabric" },
  //     { dataKey: "mill", title: "Mill" }
  //   ];

  //   const list = reportList
  //     .map(e => ({
  //       ...e,
  //       avgRollLength: e.rollCount > 0 ? e.available / (e.rollCount - e.splitCount) : 0,
  //       fabric: toFixed(e.available, 0),
  //       color: e.color.substring(0, 20)
  //     }))
  //     .filter(e => e.available > 500 && e.avgRollLength < 90)
  //     .sort((a, b) => a.avgRollLength - b.avgRollLength)
  //     .slice(0, rowCount)
  //     .map(e => ({ ...e, avgRollLength: toFixed(e.avgRollLength, 0) }));

  //   // Chart Data
  //   let rollVariation = {};
  //   reportList.forEach(e => {
  //     const rollLengthVariation = JSON.parse(e.rollLengthVariation);
  //     Object.keys(rollLengthVariation).forEach(key => {
  //       rollVariation[key] = (rollVariation[key] || 0) + rollLengthVariation[key];
  //     });
  //   });
  //   let dataLengthVariation = [
  //     { range: "0-40", qty: rollVariation["40"] },
  //     { range: "40-60", qty: rollVariation["60"] },
  //     { range: "60-80", qty: rollVariation["80"] },
  //     { range: "80-100", qty: rollVariation["100"] },
  //     { range: "100+", qty: rollVariation["120"] }
  //   ];

  //   const totalQty = dataLengthVariation.reduce((acc, el) => acc + el.qty, 0);
  //   dataLengthVariation = dataLengthVariation.map(e => ({
  //     ...e,
  //     qtyPercent: parseFloat(toFixed((e.qty * 100) / totalQty, 1))
  //   }));

  //   options[DataType.LENGTH_VARIATION] = {
  //     columns,
  //     rows: list,
  //     chartOptions: {
  //       tooltip: {
  //         trigger: "axis"
  //       },
  //       calculable: true,
  //       xAxis: [
  //         {
  //           type: "category",
  //           data: dataLengthVariation.map(e => e.range),
  //           axisLabel: {
  //             rotate: 45
  //           }
  //         }
  //       ],
  //       yAxis: [
  //         {
  //           type: "value",
  //           show: false
  //         }
  //       ],
  //       series: [
  //         {
  //           name: "Length Variation",
  //           type: "bar",
  //           data: dataLengthVariation.map(e => e.qtyPercent),
  //           barWidth: 25,
  //           itemStyle: {
  //             normal: {
  //               color: colorSecondary.main,
  //               label: {
  //                 show: true,
  //                 position: "top",
  //                 formatter: "{c}%"
  //               }
  //             }
  //           }
  //         }
  //       ]
  //     }
  //   };
  // }

  if (dataTypeList.includes(DataType.CUT_ANALYSIS) || dataTypeList.includes(DataType.LOW_CUT_PERCENT)) {
    let list = reportList
      // .filter(e => e.lot === e.noOfLots)
      .map(e => ({ ...e, month: moment(e.orderCloseDate).format("MMM, YY") }));

    let cutSummary = {
      "-6": { poCount: 0, cutQty: 0 },
      "-4": { poCount: 0, cutQty: 0 },
      "-2": { poCount: 0, cutQty: 0 },
      0: { poCount: 0, cutQty: 0 },
      "+2": { poCount: 0, cutQty: 0 },
      "+4": { poCount: 0, cutQty: 0 },
      "+6": { poCount: 0, cutQty: 0 }
    };
    let totalQty = 0;
    list.forEach(order => {
      const { cutOrderQty, cutQty } = order;
      totalQty += cutQty;
      const cutPercent = parseFloat(toFixed((cutQty * 100) / cutOrderQty, 2)) - 100.0;
      if (cutPercent < -5.0) {
        cutSummary["-6"] = {
          poCount: cutSummary["-6"]["poCount"] + 1,
          cutQty: cutSummary["-6"]["cutQty"] + cutQty
        };
      } else if (cutPercent < -3.0) {
        cutSummary["-4"] = {
          poCount: cutSummary["-4"]["poCount"] + 1,
          cutQty: cutSummary["-4"]["cutQty"] + cutQty
        };
      } else if (cutPercent < -1.0) {
        cutSummary["-2"] = {
          poCount: cutSummary["-2"]["poCount"] + 1,
          cutQty: cutSummary["-2"]["cutQty"] + cutQty
        };
      } else if (cutPercent < 1.0) {
        cutSummary["0"] = {
          poCount: cutSummary["0"]["poCount"] + 1,
          cutQty: cutSummary["0"]["cutQty"] + cutQty
        };
      } else if (cutPercent < 3.0) {
        cutSummary["+2"] = {
          poCount: cutSummary["+2"]["poCount"] + 1,
          cutQty: cutSummary["+2"]["cutQty"] + cutQty
        };
      } else if (cutPercent < 5.0) {
        cutSummary["+4"] = {
          poCount: cutSummary["+4"]["poCount"] + 1,
          cutQty: cutSummary["+4"]["cutQty"] + cutQty
        };
      } else {
        cutSummary["+6"] = {
          poCount: cutSummary["+6"]["poCount"] + 1,
          cutQty: cutSummary["+6"]["cutQty"] + cutQty
        };
      }
    });

    const dataCutDeviation = [
      {
        cutPercent: "Below -5%",
        ...cutSummary["-6"],
        "cutQty(%)": parseFloat(toFixed((cutSummary["-6"].cutQty * 100) / totalQty, 1))
      },
      {
        cutPercent: "-5% to -3%",
        ...cutSummary["-4"],
        "cutQty(%)": parseFloat(toFixed((cutSummary["-4"].cutQty * 100) / totalQty, 1))
      },
      {
        cutPercent: "-3% to -1%",
        ...cutSummary["-2"],
        "cutQty(%)": parseFloat(toFixed((cutSummary["-2"].cutQty * 100) / totalQty, 1))
      },
      {
        cutPercent: "-1% to +1%",
        ...cutSummary["0"],
        "cutQty(%)": parseFloat(toFixed((cutSummary["0"].cutQty * 100) / totalQty, 1))
      },
      {
        cutPercent: "+1% to +3%",
        ...cutSummary["+2"],
        "cutQty(%)": parseFloat(toFixed((cutSummary["+2"].cutQty * 100) / totalQty, 1))
      },
      {
        cutPercent: "+3% to +5%",
        ...cutSummary["+4"],
        "cutQty(%)": parseFloat(toFixed((cutSummary["+4"].cutQty * 100) / totalQty, 1))
      },
      {
        cutPercent: "Above +5%",
        ...cutSummary["+6"],
        "cutQty(%)": parseFloat(toFixed((cutSummary["+6"].cutQty * 100) / totalQty, 1))
      }
    ];

    options[DataType.CUT_ANALYSIS] = {
      tooltip: {
        trigger: "axis"
      },
      calculable: true,
      xAxis: [
        {
          type: "category",
          data: dataCutDeviation.map(e => e.cutPercent),
          axisLabel: {
            rotate: 45
          }
        }
      ],
      yAxis: [
        {
          type: "value",
          show: false
        }
      ],
      series: [
        {
          name: "Cut Percent Deviation",
          type: "bar",
          barWidth: 25,
          data: dataCutDeviation.map(e => e["cutQty(%)"]),
          itemStyle: {
            normal: {
              color: colorSecondary.main,
              label: {
                show: true,
                position: "top",
                formatter: "{c}%"
              }
            }
          }
        }
      ]
    };

    const rows = list
      .map(e => ({ ...e, cutPercent: (e.cutQty * 100) / e.cutOrderQty }))
      .filter(e => e.lot === e.noOfLots && e.cutPercent < 100)
      .sort((a, b) => a.cutPercent - b.cutPercent)
      .map(e => ({
        ...e,
        title: e.poRef,
        subTitle: `${e.color.substring(0, 30)}, ${e.cutOrderQty} Pcs`,
        rightTitle: `${toFixed(e.cutPercent, 1)}%`
      }))
      .slice(0, 5);
    options[DataType.LOW_CUT_PERCENT] = rows;
  }

  return options;
};
